import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Text, Button, ButtonGroup, StatusChip } from '@fragments';
import { IconCode, IconKey, IconListDetails, IconPlus, IconSend, IconUser, IconWebhook, IconX } from '@tabler/icons-react';
import {
    WEBHOOK_METHODS,
    getWebhookAuthTypes,
    getWebhookContentTypes,
    getWebhookFieldSources,
} from '../../../enums/actionTypes';
import JsonTemplateEditor from '../JsonTemplateEditor';
import './styles.scss';

const METHOD_OPTIONS = WEBHOOK_METHODS.map(m => ({ value: m, label: m }));

const isMasked = value => typeof value === 'string' && value.startsWith('***');

function secretHelper(t, value) {
    if (!isMasked(value)) return undefined;
    return t('webhookBuilder.secretHelper');
}

function staticFieldsToRows(staticFields) {
    return Object.entries(staticFields ?? {}).map(([key, value]) => ({ key, value: String(value ?? '') }));
}

function rowsToStaticFields(rows) {
    const out = {};
    rows.forEach(({ key, value }) => {
        if (key.trim()) out[key.trim()] = value;
    });
    return out;
}

function TestResult({ testState }) {
    const { t } = useTranslation('integrations');

    if (!testState || testState.loading) return null;

    if (testState.error) {
        return (
            <div className="webhook-builder__test-result">
                <div className="webhook-builder__test-result-header">
                    <StatusChip.Error text={t('webhookBuilder.test.failed')} />
                </div>
                <Text.span contrast={7}>{testState.error}</Text.span>
            </div>
        );
    }

    if (!testState.result) return null;

    const { response_code: code, response_body: body } = testState.result;
    const succeeded = code >= 200 && code < 300;
    const Chip = succeeded ? StatusChip.Success : StatusChip.Error;

    return (
        <div className="webhook-builder__test-result">
            <div className="webhook-builder__test-result-header">
                <Chip text={`HTTP ${code}`} />
                <Text.caption>{t('webhookBuilder.test.responseTitle')}</Text.caption>
            </div>
            {body
                ? <pre className="webhook-builder__test-result-body">{body}</pre>
                : <Text.span contrast={5}>{t('webhookBuilder.test.emptyBody')}</Text.span>}
        </div>
    );
}

export default function WebhookBuilder({ config, onChange, onTest, testState }) {
    const { t } = useTranslation('integrations');
    const auth = config?.auth ?? { type: 'none' };
    const headers = config?.headers ?? [];
    const fieldMappings = config?.field_mappings ?? [];

    // static_fields is an object in the contract; rows are local UI state so
    // keys stay editable without churning object identity on each keystroke.
    const [staticRows, setStaticRows] = useState(() => staticFieldsToRows(config?.static_fields));

    // The backend uses body_template whenever it is set, ignoring mappings —
    // the mode switch makes that either/or explicit. The draft keeps the
    // template text alive while the user is on the mapping side (config only
    // carries it in template mode, so what you see is what fires).
    const [bodyMode, setBodyMode] = useState(() => (config?.body_template ? 'template' : 'mapping'));
    const [templateDraft, setTemplateDraft] = useState(() => config?.body_template ?? '');

    function handleBodyModeChange(mode) {
        if (mode === bodyMode) return;
        setBodyMode(mode);
        patch({
            body_template: mode === 'template' && templateDraft.trim() ? templateDraft : null,
        });
    }

    function handleTemplateChange(v) {
        setTemplateDraft(v);
        patch({ body_template: v.trim() ? v : null });
    }

    // Full validation runs on click (useManageIntegration); the button only
    // gates on the two fields without which a test is meaningless.
    const canTest = /^https:\/\/./i.test((config?.url ?? '').trim())
        && WEBHOOK_METHODS.includes(config?.method);

    function patch(partial) {
        onChange({ ...config, ...partial });
    }

    function patchAuth(partial) {
        patch({ auth: { ...auth, ...partial } });
    }

    // Switching auth type discards previous credentials so stale secrets
    // (possibly masked) never leak into another auth shape on submit.
    function handleAuthTypeChange(type) {
        patch({ auth: { type } });
    }

    function updateRow(list, index, partial) {
        return list.map((row, i) => (i === index ? { ...row, ...partial } : row));
    }

    function updateStaticRows(next) {
        setStaticRows(next);
        patch({ static_fields: rowsToStaticFields(next) });
    }

    return (
        <div className="webhook-builder">
            {/* ── Endpoint ─────────────────────────────────────────────── */}
            <div className="webhook-builder__section">
                <Text.subheading contrast={6}>{t('webhookBuilder.endpoint.title')}</Text.subheading>
                <div className="webhook-builder__endpoint-row">
                    <div className="webhook-builder__method">
                        <Input.Select
                            label={t('webhookBuilder.endpoint.methodLabel')}
                            value={config?.method ?? 'POST'}
                            onInput={v => patch({ method: v })}
                            options={METHOD_OPTIONS}
                        />
                    </div>
                    <div className="webhook-builder__url">
                        <Input.Text
                            label={t('webhookBuilder.endpoint.urlLabel')}
                            placeholder={t('webhookBuilder.endpoint.urlPlaceholder')}
                            value={config?.url ?? ''}
                            onInput={v => patch({ url: v })}
                            start={<IconWebhook size={16} />}
                        />
                    </div>
                </div>
            </div>

            {/* ── Autenticação ─────────────────────────────────────────── */}
            <div className="webhook-builder__section">
                <Text.subheading contrast={6}>{t('webhookBuilder.auth.title')}</Text.subheading>
                <Input.Select
                    label={t('webhookBuilder.auth.typeLabel')}
                    value={auth.type ?? 'none'}
                    onInput={handleAuthTypeChange}
                    options={getWebhookAuthTypes(t)}
                />

                {auth.type === 'bearer' && (
                    <Input.Text
                        label={t('webhookBuilder.auth.tokenLabel')}
                        placeholder={t('webhookBuilder.auth.tokenPlaceholder')}
                        value={auth.token ?? ''}
                        onInput={v => patchAuth({ token: v })}
                        start={<IconKey size={16} />}
                        helper={secretHelper(t, auth.token)}
                    />
                )}

                {auth.type === 'basic' && (
                    <>
                        <Input.Text
                            label={t('webhookBuilder.auth.usernameLabel')}
                            placeholder={t('webhookBuilder.auth.usernamePlaceholder')}
                            value={auth.username ?? ''}
                            onInput={v => patchAuth({ username: v })}
                            start={<IconUser size={16} />}
                        />
                        <Input.Text
                            label={t('webhookBuilder.auth.passwordLabel')}
                            placeholder={t('webhookBuilder.auth.passwordPlaceholder')}
                            value={auth.password ?? ''}
                            onInput={v => patchAuth({ password: v })}
                            start={<IconKey size={16} />}
                            helper={secretHelper(t, auth.password)}
                        />
                    </>
                )}

                {auth.type === 'api_key' && (
                    <>
                        <Input.Text
                            label={t('webhookBuilder.auth.headerNameLabel')}
                            placeholder="X-Api-Key"
                            value={auth.header_name ?? ''}
                            onInput={v => patchAuth({ header_name: v })}
                        />
                        <Input.Text
                            label={t('common:fields.value')}
                            placeholder={t('webhookBuilder.auth.valuePlaceholder')}
                            value={auth.value ?? ''}
                            onInput={v => patchAuth({ value: v })}
                            start={<IconKey size={16} />}
                            helper={secretHelper(t, auth.value)}
                        />
                    </>
                )}
            </div>

            {/* ── Cabeçalhos ───────────────────────────────────────────── */}
            <div className="webhook-builder__section">
                <div className="webhook-builder__section-header">
                    <Text.subheading contrast={6}>{t('webhookBuilder.headers.title')}</Text.subheading>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={() => patch({ headers: [...headers, { key: '', value: '' }] })}
                        color="overlay"
                        variant="dashed"
                        size="small"
                    >
                        {t('common:actions.add')}
                    </Button>
                </div>

                {headers.length === 0 ? (
                    <Text.span contrast={5}>{t('webhookBuilder.headers.empty')}</Text.span>
                ) : headers.map((header, idx) => (
                    <div className="webhook-builder__kv-row" key={idx}>
                        <Input.Text
                            placeholder={t('webhookBuilder.headers.keyPlaceholder')}
                            value={header.key ?? ''}
                            onInput={v => patch({ headers: updateRow(headers, idx, { key: v }) })}
                        />
                        <Input.Text
                            placeholder={t('webhookBuilder.headers.valuePlaceholder')}
                            value={header.value ?? ''}
                            onInput={v => patch({ headers: updateRow(headers, idx, { value: v }) })}
                        />
                        <Button
                            onClick={() => patch({ headers: headers.filter((_, i) => i !== idx) })}
                            icon={<IconX size={16} />}
                            color="overlay"
                            size="small"
                            tooltip={t('webhookBuilder.headers.removeTooltip')}
                        />
                    </div>
                ))}
            </div>

            {/* ── Corpo / Mapeamento ───────────────────────────────────── */}
            <div className="webhook-builder__section">
                <div className="webhook-builder__section-header">
                    <Text.subheading contrast={6}>{t('webhookBuilder.body.title')}</Text.subheading>
                </div>

                <Input.Select
                    label={t('webhookBuilder.body.contentTypeLabel')}
                    value={config?.content_type ?? 'application/json'}
                    onInput={v => patch({ content_type: v })}
                    options={getWebhookContentTypes(t)}
                />

                <div className="webhook-builder__subsection-header pt-1">
                    <Text.label contrast={8}>{t('webhookBuilder.body.modeLabel')}</Text.label>
                </div>
                <ButtonGroup
                    grow
                    size="small"
                    value={bodyMode}
                    onChange={handleBodyModeChange}
                    options={[
                        { value: 'mapping', label: t('webhookBuilder.body.modeMapping'), icon: <IconListDetails size={16} /> },
                        { value: 'template', label: t('webhookBuilder.body.modeTemplate'), icon: <IconCode size={16} /> },
                    ]}
                />
                <Text.span contrast={5}>
                    {bodyMode === 'mapping'
                        ? t('webhookBuilder.body.mappingHint')
                        : t('webhookBuilder.body.templateHint', { token: '{{...}}' })}
                </Text.span>

                {bodyMode === 'mapping' && (<>
                <div className="webhook-builder__subsection-header">
                    <Text.label contrast={8}>{t('webhookBuilder.mapping.title')}</Text.label>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={() => patch({ field_mappings: [...fieldMappings, { source: '', target: '' }] })}
                        color="overlay"
                        variant="dashed"
                        size="small"
                    >
                        {t('common:actions.add')}
                    </Button>
                </div>
                <Text.span contrast={5}>
                    {t('webhookBuilder.mapping.hint')}
                </Text.span>

                {fieldMappings.map((mapping, idx) => (
                    <div className="webhook-builder__kv-row" key={idx}>
                        <Input.Select
                            placeholder={t('webhookBuilder.mapping.sourcePlaceholder')}
                            value={mapping.source ?? ''}
                            onInput={v => patch({ field_mappings: updateRow(fieldMappings, idx, { source: v }) })}
                            options={getWebhookFieldSources(t)}
                        />
                        <Input.Text
                            placeholder={t('webhookBuilder.mapping.targetPlaceholder')}
                            value={mapping.target ?? ''}
                            onInput={v => patch({ field_mappings: updateRow(fieldMappings, idx, { target: v }) })}
                        />
                        <Button
                            onClick={() => patch({ field_mappings: fieldMappings.filter((_, i) => i !== idx) })}
                            icon={<IconX size={16} />}
                            color="overlay"
                            size="small"
                            tooltip={t('webhookBuilder.mapping.removeTooltip')}
                        />
                    </div>
                ))}

                <div className="webhook-builder__subsection-header">
                    <Text.label contrast={8}>{t('webhookBuilder.staticFields.title')}</Text.label>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={() => updateStaticRows([...staticRows, { key: '', value: '' }])}
                        color="overlay"
                        variant="dashed"
                        size="small"
                    >
                        {t('common:actions.add')}
                    </Button>
                </div>
                <Text.span contrast={5}>
                    {t('webhookBuilder.staticFields.hint')}
                </Text.span>

                {staticRows.map((row, idx) => (
                    <div className="webhook-builder__kv-row" key={idx}>
                        <Input.Text
                            placeholder={t('webhookBuilder.staticFields.keyPlaceholder')}
                            value={row.key}
                            onInput={v => updateStaticRows(updateRow(staticRows, idx, { key: v }))}
                        />
                        <Input.Text
                            placeholder={t('webhookBuilder.staticFields.valuePlaceholder')}
                            value={row.value}
                            onInput={v => updateStaticRows(updateRow(staticRows, idx, { value: v }))}
                        />
                        <Button
                            onClick={() => updateStaticRows(staticRows.filter((_, i) => i !== idx))}
                            icon={<IconX size={16} />}
                            color="overlay"
                            size="small"
                            tooltip={t('webhookBuilder.staticFields.removeTooltip')}
                        />
                    </div>
                ))}
                </>)}

                {bodyMode === 'template' && (
                    <JsonTemplateEditor
                        placeholder={'{ "cliente": "{{name}}", "cobranca": "{{payment.id}}" }'}
                        value={templateDraft}
                        onChange={handleTemplateChange}
                        variables={getWebhookFieldSources(t)}
                        helper={t('jsonEditor.helper', { token: '{{' })}
                    />
                )}
            </div>

            {/* ── Teste ────────────────────────────────────────────────── */}
            {onTest && (
                <div className="webhook-builder__section">
                    <div className="webhook-builder__section-header">
                        <Text.subheading contrast={6}>{t('webhookBuilder.test.title')}</Text.subheading>
                        <Button
                            startIcon={<IconSend size={16} />}
                            onClick={onTest}
                            color="overlay"
                            size="small"
                            loading={testState?.loading}
                            disabled={!canTest}
                        >
                            {t('webhookBuilder.test.button')}
                        </Button>
                    </div>
                    <Text.span contrast={5}>
                        {t('webhookBuilder.test.description')}
                    </Text.span>
                    <TestResult testState={testState} />
                </div>
            )}
        </div>
    );
}
